/**
 * TaskUpdateChangeCard - Rich card display for a single task update's changes.
 *
 * Task updates are comments/activity logs. Layout:
 * - Header with update type badge, visibility badge
 * - Full update text as primary content
 * - User/agent attribution
 * - Collapsible reference IDs section
 * - Transfer checkboxes + "Mark as Transferred" button
 * - UPDATED/SAME: unchanged content behind ShowMoreSection
 */

import { Activity, Bot, MessageSquare, User } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import type {
  ChangedTaskUpdateEntity,
} from '@/services/api/generated/crossMatterChangesService';

import {
  buildCheckableItems,
  type CheckableFieldConfig,
} from '../utils/buildCheckableItems';
import { formatEnumValue } from '../utils/formatting';
import { useEntityChanges } from '../utils/useEntityChanges';
import { useTransferAction } from '../utils/useTransferAction';
import { useTransferChecklist } from '../utils/useTransferChecklist';
import { ChangedFieldHighlight } from './ChangedFieldHighlight';
import { CHANGE_TYPE_CONFIG } from './changeTypeConfig';
import { CollapsibleSection } from './CollapsibleSection';
import { EntityChangeCardShell } from './EntityChangeCardShell';
import { EntityField } from './EntityField';
import { EventsFallbackWithChecklist } from './EventsFallbackWithChecklist';
import { ShowMoreSection } from './ShowMoreSection';
import { TransferButton } from './TransferButton';
import { TransferCheckbox } from './TransferCheckbox';

// ============================================================================
// Task Update config
// ============================================================================

const REFERENCE_FIELDS: { key: string; label: string }[] = [
  { key: 'task_id', label: 'Task ID' },
  { key: 'correlation_id', label: 'Correlation ID' },
];

const TASK_UPDATE_CHECKABLE_FIELDS: CheckableFieldConfig[] = [
  { key: 'update', type: 'field' },
  { key: 'update_type', type: 'field' },
  { key: 'visibility', type: 'field' },
  { key: 'user', type: 'nested' },
  { key: 'agent', type: 'field' },
  { key: 'agent_tags', type: 'nested' },
  { key: 'task_id', type: 'field' },
  { key: 'correlation_id', type: 'field' },
];

function getUserDisplayName(user: {
  first_name?: string | null;
  last_name?: string | null;
  username: string;
}): string {
  const parts = [user.first_name, user.last_name].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : user.username;
}

// ============================================================================
// Main Component
// ============================================================================

export const TaskUpdateChangeCard: React.FC<{
  entity: ChangedTaskUpdateEntity;
  /** When true, hides TransferButton and reports readiness via callback */
  nested?: boolean;
  /** When true, renders a compact single-line row instead of full card */
  condensed?: boolean;
  /** Called when nested card's transfer readiness changes */
  onTransferReady?: (entityId: string, ready: boolean) => void;
}> = ({ entity, nested, condensed, onTransferReady }) => {
  const displayData =
    entity.change_type === 'DELETED'
      ? (entity.old ?? entity.current)
      : (entity.current ?? entity.old);

  const changes = useEntityChanges(
    entity.change_type,
    entity.old,
    entity.current,
  );

  const checkableItems = React.useMemo<string[]>(
    () =>
      buildCheckableItems(
        entity.change_type,
        displayData as Record<string, unknown> | undefined,
        entity.events,
        changes,
        TASK_UPDATE_CHECKABLE_FIELDS,
      ),
    [entity.change_type, entity.events, changes, displayData],
  );

  const checklist = useTransferChecklist(entity.change_type, checkableItems);
  const transferEventIds = React.useMemo(() => entity.events.map((e) => e.id), [entity.events]);
  const transfer = useTransferAction(transferEventIds);

  // Report readiness to parent when nested
  React.useEffect(() => {
    if (nested && onTransferReady) {
      onTransferReady(entity.entity_id, checklist.buttonEnabled);
    }
  }, [nested, onTransferReady, entity.entity_id, checklist.buttonEnabled]);

  if (!displayData) return null;

  const data = displayData as unknown as Record<string, unknown>;

  // Fields
  const updateText = displayData.update;
  const updateType = displayData.update_type;
  const { visibility } = displayData;
  const { user } = displayData;
  const { agent } = displayData;
  const agentTags = displayData.agent_tags ?? [];

  // Icon based on update type
  const IconComponent = updateType === 'activity' ? Activity : MessageSquare;

  // ── Condensed mode: compact single-line row ──────────────────────────
  if (condensed) {
    const hasChanges = entity.change_type !== 'SAME';
    return (
      <div
        className={`flex items-start gap-2 py-1.5 px-2 rounded ${
          hasChanges ? 'bg-amber-50/40' : ''
        }`}
      >
        <IconComponent className="h-3.5 w-3.5 text-gray-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <span className="text-sm text-gray-700 line-clamp-2">
            {updateText || entity.name}
          </span>
          {user != null && (
            <span className="text-xs text-gray-400 ml-1">
              —{' '}
              {getUserDisplayName(
                user as {
                  first_name?: string | null;
                  last_name?: string | null;
                  username: string;
                },
              )}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {updateType && (
            <Badge color="zinc" className="text-[10px]">
              {formatEnumValue(updateType)}
            </Badge>
          )}
          {hasChanges && (
            <Badge
              color={CHANGE_TYPE_CONFIG[entity.change_type].color}
              className="text-[10px]"
            >
              {CHANGE_TYPE_CONFIG[entity.change_type].label}
            </Badge>
          )}
        </div>
      </div>
    );
  }

  // ── Full card mode ───────────────────────────────────────────────────

  // Change detection
  const updateChanged = changes.isFieldChanged('update');
  const updateTypeChanged = changes.isFieldChanged('update_type');
  const visibilityChanged = changes.isFieldChanged('visibility');
  const userChanged =
    changes.isFieldChanged('user') || changes.hasChangesUnder('user');
  const agentChanged = changes.isFieldChanged('agent');
  const agentTagsChanged =
    changes.isFieldChanged('agent_tags') ||
    changes.hasChangesUnder('agent_tags');

  // Attribution
  const hasUser = user != null;
  const hasAgent = agent != null;
  const hasAgentTags = agentTags.length > 0;
  const hasAttribution = hasUser || hasAgent || hasAgentTags;
  const attributionChanged = userChanged || agentChanged || agentTagsChanged;

  // Reference fields
  const visibleReferenceFields = REFERENCE_FIELDS.filter(
    (f) => data[f.key] || changes.isFieldChanged(f.key),
  );
  const changedReferenceFields = visibleReferenceFields.filter((f) =>
    changes.isFieldChanged(f.key),
  );
  const unchangedReferenceFields = visibleReferenceFields.filter(
    (f) => !changes.isFieldChanged(f.key),
  );

  // Events fallback
  const isUpdatedWithoutDiffs =
    entity.change_type === 'UPDATED' && changes.totalChanges === 0;

  const hasExpandedContent =
    !!updateText ||
    updateTypeChanged ||
    visibilityChanged ||
    hasAttribution ||
    visibleReferenceFields.length > 0 ||
    isUpdatedWithoutDiffs;

  // Unchanged content for ShowMore
  const isUpdatedOrSame =
    entity.change_type === 'UPDATED' || entity.change_type === 'SAME';
  const hasUnchangedContent =
    isUpdatedOrSame &&
    ((!updateChanged && !!updateText) ||
      (!attributionChanged && hasAttribution) ||
      unchangedReferenceFields.length > 0);

  return (
    <EntityChangeCardShell
      changeType={entity.change_type}
      name={entity.name}
      icon={IconComponent}
      iconBgClassName="bg-slate-50"
      iconTextClassName="text-slate-600"
      badges={
        <>
          {updateType && (
            <Badge color="zinc" className="text-[10px] shrink-0">
              {formatEnumValue(updateType)}
            </Badge>
          )}
          {visibility && (
            <Badge
              color={visibility === 'Public' ? 'blue' : 'zinc'}
              className="text-[10px] shrink-0"
            >
              {visibility}
            </Badge>
          )}
        </>
      }
      changeCount={changes.totalChanges}
      eventCount={entity.events.length}
      checklist={checklist}
      hasExpandedContent={hasExpandedContent}
    >
      {/* Events fallback */}
      {isUpdatedWithoutDiffs && (
        <EventsFallbackWithChecklist
          events={entity.events}
          checklist={checklist}
        />
      )}

      {/* Changed header fields */}
      {updateTypeChanged && updateType && (
        <ChangedFieldHighlight
          label="Update Type"
          fieldKey="update_type"
          changes={changes}
          checklist={checklist}
        >
          <span className="text-sm text-gray-900">
            {formatEnumValue(updateType)}
          </span>
        </ChangedFieldHighlight>
      )}

      {visibilityChanged && visibility && (
        <ChangedFieldHighlight
          label="Visibility"
          fieldKey="visibility"
          changes={changes}
          checklist={checklist}
        >
          <Badge
            color={visibility === 'Public' ? 'blue' : 'zinc'}
            className="text-xs"
          >
            {visibility}
          </Badge>
        </ChangedFieldHighlight>
      )}

      {/* Update text — primary content */}
      {updateChanged && updateText ? (
        <ChangedFieldHighlight
          label="Update"
          fieldKey="update"
          changes={changes}
          checklist={checklist}
        >
          <p className="text-sm text-gray-900 whitespace-pre-wrap">
            {updateText}
          </p>
        </ChangedFieldHighlight>
      ) : (
        (entity.change_type === 'CREATED' ||
          entity.change_type === 'DELETED') &&
        updateText && (
          <p className="text-sm text-gray-700 whitespace-pre-wrap px-1">
            {updateText}
          </p>
        )
      )}

      {/* Attribution — changed */}
      {attributionChanged && hasAttribution && (
        <div className="flex items-center gap-2 text-xs text-gray-500 px-1">
          {hasUser && userChanged && (
            <ChangedFieldHighlight
              label="User"
              fieldKey="user"
              changes={changes}
              checklist={checklist}
            >
              <span className="flex items-center gap-1 text-sm text-gray-900">
                <User className="h-3 w-3 text-gray-400" />
                {getUserDisplayName(
                  user as {
                    first_name?: string | null;
                    last_name?: string | null;
                    username: string;
                  },
                )}
              </span>
            </ChangedFieldHighlight>
          )}
          {hasAgent && agentChanged && (
            <ChangedFieldHighlight
              label="Agent"
              fieldKey="agent"
              changes={changes}
              checklist={checklist}
            >
              <span className="flex items-center gap-1 text-sm text-gray-900">
                <Bot className="h-3 w-3 text-gray-400" />
                {formatEnumValue(agent as string)}
              </span>
            </ChangedFieldHighlight>
          )}
          {hasAgentTags && agentTagsChanged && (
            <ChangedFieldHighlight
              label="Agent Tags"
              fieldKey="agent_tags"
              changes={changes}
              checklist={checklist}
            >
              <div className="flex items-center gap-1 flex-wrap">
                {(agentTags as string[]).map((tag) => (
                  <Badge key={tag} color="zinc" className="text-[10px]">
                    {tag}
                  </Badge>
                ))}
              </div>
            </ChangedFieldHighlight>
          )}
        </div>
      )}

      {/* Attribution — not changed, show for CREATED/DELETED */}
      {!attributionChanged &&
        hasAttribution &&
        (entity.change_type === 'CREATED' ||
          entity.change_type === 'DELETED') && (
          <div className="flex items-center gap-2 text-xs text-gray-500 px-1">
            {hasUser && (
              <span className="flex items-center gap-1">
                <User className="h-3 w-3 text-gray-400" />
                {getUserDisplayName(
                  user as {
                    first_name?: string | null;
                    last_name?: string | null;
                    username: string;
                  },
                )}
              </span>
            )}
            {hasAgent && (
              <span className="flex items-center gap-1">
                <Bot className="h-3 w-3 text-gray-400" />
                {formatEnumValue(agent as string)}
              </span>
            )}
            {hasAgentTags &&
              (agentTags as string[]).map((tag) => (
                <Badge key={tag} color="zinc" className="text-[10px]">
                  {tag}
                </Badge>
              ))}
          </div>
        )}

      {/* Changed reference fields */}
      {changedReferenceFields.length > 0 && (
        <CollapsibleSection
          title="References"
          count={changedReferenceFields.length}
          hasChanges
          changeType={entity.change_type}
        >
          {changedReferenceFields.map((f) => (
            <div key={f.key} className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <EntityField
                  label={f.label}
                  value={data[f.key]}
                  fieldKey={f.key}
                  changes={changes}
                />
              </div>
              {checklist.showCheckboxes && (
                <TransferCheckbox
                  checked={checklist.isChecked(f.key)}
                  onToggle={() => checklist.toggle(f.key)}
                  className="mt-2.5"
                />
              )}
            </div>
          ))}
        </CollapsibleSection>
      )}

      {/* ShowMore — unchanged content for UPDATED/SAME */}
      {hasUnchangedContent && (
        <ShowMoreSection changeType={entity.change_type}>
          {/* Unchanged update text */}
          {!updateChanged && !!updateText && (
            <p className="text-sm text-gray-700 whitespace-pre-wrap px-1">
              {updateText}
            </p>
          )}

          {/* Unchanged attribution */}
          {!attributionChanged && hasAttribution && (
            <div className="flex items-center gap-2 text-xs text-gray-500 px-1">
              {hasUser && (
                <span className="flex items-center gap-1">
                  <User className="h-3 w-3 text-gray-400" />
                  {getUserDisplayName(
                    user as {
                      first_name?: string | null;
                      last_name?: string | null;
                      username: string;
                    },
                  )}
                </span>
              )}
              {hasAgent && (
                <span className="flex items-center gap-1">
                  <Bot className="h-3 w-3 text-gray-400" />
                  {formatEnumValue(agent as string)}
                </span>
              )}
              {hasAgentTags &&
                (agentTags as string[]).map((tag) => (
                  <Badge key={tag} color="zinc" className="text-[10px]">
                    {tag}
                  </Badge>
                ))}
            </div>
          )}

          {/* Unchanged reference fields */}
          {unchangedReferenceFields.length > 0 && (
            <CollapsibleSection
              title="References"
              count={unchangedReferenceFields.length}
              hasChanges={false}
              changeType={entity.change_type}
            >
              {unchangedReferenceFields.map((f) => (
                <div key={f.key} className="flex items-start">
                  <div className="flex-1 min-w-0">
                    <EntityField
                      label={f.label}
                      value={data[f.key]}
                      fieldKey={f.key}
                      changes={changes}
                    />
                  </div>
                </div>
              ))}
            </CollapsibleSection>
          )}
        </ShowMoreSection>
      )}

      {/* Transfer button (hidden when nested — parent owns the button) */}
      {!nested && checklist.showButton && (
        <TransferButton
          enabled={checklist.buttonEnabled}
          onClick={transfer.handleTransfer}
          onSkip={transfer.handleSkip}
          isLoading={transfer.isLoading}
          status={transfer.status}
        />
      )}
    </EntityChangeCardShell>
  );
};
